import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/auth'
import { listInquiries, updateInquiry, addInquiryNote, listApplications, updateApplication, listUsers } from '../../lib/api'
import UsersTab from './UsersTab'
import ScheduleTab from './ScheduleTab'

const statusColors = { new: '#967057', contacted: '#6B8B75', scheduled: '#00564F', closed: '#D0C9B7' }

const selectStyle = {
  fontFamily: 'Inter, Arial, sans-serif', fontSize: 15, color: '#191917', background: '#FFFFFF',
  border: '1px solid #D0C9B7', borderRadius: 4, padding: '8px 10px', boxSizing: 'border-box', width: '100%', maxWidth: '100%',
}
const textareaStyle = { ...selectStyle }
const smallLabel = { fontSize: 12, fontWeight: 600, color: '#527569', display: 'block' }
const ghostBtn = {
  background: 'transparent', color: '#00564F', border: '1px solid #00564F', borderRadius: 4,
  padding: '12px 16px', minHeight: 48, fontWeight: 600, fontSize: 14,
  fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer',
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch {
    return iso
  }
}

export default function AdminPortal() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()

  const [tab, setTab] = useState('inquiries')
  const [inquiries, setInquiries] = useState([])
  const [applications, setApplications] = useState([])
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState({})
  const [drafts, setDrafts] = useState({})

  const load = async () => {
    try {
      const [i, a, u] = await Promise.all([listInquiries(), listApplications(), listUsers()])
      setInquiries(i.inquiries)
      setApplications(a.applications)
      setStaff(u.users.filter((x) => x.role === 'admin' && x.is_active))
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const guard = async (fn) => {
    setBusy(true)
    try {
      await fn()
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const patchInquiry = (id, body) =>
    guard(async () => {
      const { inquiry } = await updateInquiry(id, body)
      setInquiries((list) => list.map((x) => (x.id === id ? { ...x, ...inquiry } : x)))
    })

  const patchApplication = (id, body) =>
    guard(async () => {
      const { application } = await updateApplication(id, body)
      setApplications((list) => list.map((x) => (x.id === id ? { ...x, ...application } : x)))
    })

  const postNote = (id) =>
    guard(async () => {
      const body = (drafts[id] || '').trim()
      if (!body) return
      const { note } = await addInquiryNote(id, body)
      setInquiries((list) =>
        list.map((x) =>
          x.id === id
            ? { ...x, notes: [{ id: note.id, body: note.body, authorName: note.author_name, createdAt: note.created_at }, ...(x.notes || [])] }
            : x
        )
      )
      setDrafts((d) => ({ ...d, [id]: '' }))
    })

  const stats = [
    ['New inquiries', inquiries.filter((i) => i.status === 'new').length],
    ['Contacted', inquiries.filter((i) => i.status === 'contacted').length],
    ['Assessments scheduled', inquiries.filter((i) => i.status === 'scheduled').length],
    ['New applications', applications.filter((a) => a.status === 'new').length],
  ]

  const tabDefs = [
    ['inquiries', 'Care Inquiries'],
    ['applications', 'Career Applications'],
    ['calendar', 'Assessment Calendar'],
    ['schedule', 'Schedule & Tasks'],
    ['users', 'Users & Access'],
  ]

  const openSlots = inquiries
    .filter((i) => i.kind === 'care' && i.status !== 'closed')
    .flatMap((i) =>
      (Array.isArray(i.preferred_slots) ? i.preferred_slots : []).map((s, n) => ({
        key: `${i.id}-${n}`,
        who: `${i.first_name} ${i.last_name || ''}`.trim(),
        slot: typeof s === 'string' ? s : [s.date, s.timeBlock].filter(Boolean).join(' · '),
        status: i.status,
        assignee: i.assignee_name,
      }))
    )

  return (
    <div style={{ minHeight: '100vh', background: '#F4EDDF', color: '#191917', fontFamily: 'Inter, Arial, sans-serif', fontSize: 17, lineHeight: 1.6 }}>
      <div style={{ background: '#003F3A', color: '#FAF7F0', padding: '14px 24px' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 21, fontWeight: 600 }}>
            True North · Office Admin
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 15 }}>
            <span>Signed in as <strong>{user?.fullName}</strong></span>
            <button
              type="button"
              onClick={async () => {
                await signOut()
                navigate('/login')
              }}
              style={{ background: 'transparent', color: '#B7C4B7', border: '1px solid #527569', borderRadius: 4, padding: '8px 14px', fontWeight: 600, fontSize: 14, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '28px 24px 64px 24px' }}>
        {error && (
          <div style={{ background: '#FBEDEA', border: '1px solid #E0B4AA', color: '#8A2C1B', borderRadius: 8, padding: 16, marginBottom: 20 }} role="alert">
            {error}
          </div>
        )}

        <div className="grid grid-4" style={{ gap: 16, marginBottom: 24 }}>
          {stats.map(([label, value]) => (
            <div key={label} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 24px)' }}>
              <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 34, fontWeight: 600, color: '#003F3A', lineHeight: 1 }}>
                {loading ? '—' : value}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#527569', marginTop: 8 }}>
                {label}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          {tabDefs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              style={{
                background: tab === key ? '#00564F' : '#FAF7F0', color: tab === key ? '#FAF7F0' : '#191917',
                border: tab === key ? '1px solid #00564F' : '1px solid #D0C9B7',
                borderRadius: 4, padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15,
                fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && tab !== 'users' && <p style={{ color: '#5D645B' }}>Loading…</p>}

        {!loading && tab === 'inquiries' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {inquiries.length === 0 && <p style={{ color: '#5D645B' }}>No inquiries yet. Submissions from the website appear here.</p>}
            {inquiries.map((i) => (
              <div key={i.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderLeft: `5px solid ${statusColors[i.status]}`, borderRadius: 8, padding: 'clamp(18px, 4vw, 26px)' }}>
                <div className="grid" style={{ '--cols': 'minmax(0, 1.4fr) minmax(0, 1fr) 170px 170px auto', gap: 16, alignItems: 'center' }}>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <div style={{ fontWeight: 600, color: '#003F3A' }}>
                      {i.first_name} {i.last_name} · {i.reference}
                    </div>
                    <div style={{ fontSize: 14, color: '#5D645B' }}>
                      {i.kind === 'care' ? 'Care request' : 'General inquiry'} · {fmtDate(i.created_at)}
                    </div>
                    <div style={{ fontSize: 14, color: '#5D645B' }}>
                      {[i.phone, i.email, i.postal_code].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div style={{ minWidth: 0, fontSize: 15, color: '#5D645B' }}>
                    {(i.services || []).join(', ') || '—'}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={smallLabel} htmlFor={`st-${i.id}`}>STATUS</label>
                    <select id={`st-${i.id}`} style={selectStyle} value={i.status} disabled={busy} onChange={(e) => patchInquiry(i.id, { status: e.target.value })}>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={smallLabel} htmlFor={`as-${i.id}`}>ASSIGNED TO</label>
                    <select id={`as-${i.id}`} style={selectStyle} value={i.assignee_id || ''} disabled={busy} onChange={(e) => patchInquiry(i.id, { assigneeId: e.target.value || null })}>
                      <option value="">Unassigned</option>
                      {staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                    </select>
                  </div>
                  <button type="button" style={ghostBtn} onClick={() => setOpen((o) => ({ ...o, [i.id]: !o[i.id] }))}>
                    {open[i.id] ? 'Hide' : 'Details'}
                  </button>
                </div>

                {open[i.id] && (
                  <div style={{ marginTop: 18, borderTop: '1px solid #E5DFD1', paddingTop: 18 }}>
                    <div className="grid grid-2" style={{ gap: 16, fontSize: 15, color: '#5D645B' }}>
                      <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        <div><strong style={{ color: '#003F3A' }}>Who needs care:</strong> {i.who_needs_care || '—'}</div>
                        <div><strong style={{ color: '#003F3A' }}>Frequency:</strong> {i.frequency || '—'}</div>
                        <div><strong style={{ color: '#003F3A' }}>Meeting:</strong> {i.assessment_type || '—'}</div>
                        <div><strong style={{ color: '#003F3A' }}>Preferred contact:</strong> {i.preferred_contact || '—'}</div>
                      </div>
                      <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        <div><strong style={{ color: '#003F3A' }}>Preferred times:</strong></div>
                        {(Array.isArray(i.preferred_slots) ? i.preferred_slots : []).length === 0 && <div>—</div>}
                        {(Array.isArray(i.preferred_slots) ? i.preferred_slots : []).map((s, n) => (
                          <div key={n}>{typeof s === 'string' ? s : [s.date, s.timeBlock].filter(Boolean).join(' · ')}</div>
                        ))}
                      </div>
                    </div>
                    {i.message && (
                      <div style={{ marginTop: 14, background: '#EEE8DA', borderRadius: 6, padding: '14px 16px', fontSize: 15, overflowWrap: 'anywhere' }}>
                        {i.message}
                      </div>
                    )}

                    <div style={{ marginTop: 18 }}>
                      <div style={{ fontWeight: 600, color: '#003F3A', marginBottom: 8 }}>Internal notes</div>
                      {(i.notes || []).length === 0 && <div style={{ fontSize: 15, color: '#5D645B' }}>No notes yet.</div>}
                      {(i.notes || []).map((n) => (
                        <div key={n.id} style={{ fontSize: 15, color: '#5D645B', padding: '8px 0', borderBottom: '1px solid #E5DFD1', overflowWrap: 'anywhere' }}>
                          <strong style={{ color: '#003F3A' }}>{n.authorName}</strong> · {fmtDate(n.createdAt)}
                          <div>{n.body}</div>
                        </div>
                      ))}
                      <div className="grid" style={{ '--cols': 'minmax(0, 1fr) auto', gap: 10, marginTop: 12, alignItems: 'start' }}>
                        <textarea
                          style={{ ...textareaStyle, minHeight: 72 }}
                          placeholder="Add an internal note…"
                          value={drafts[i.id] || ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                        />
                        <button type="button" style={ghostBtn} disabled={busy} onClick={() => postNote(i.id)}>Add note</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'applications' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {applications.length === 0 && <p style={{ color: '#5D645B' }}>No applications yet.</p>}
            {applications.map((a) => (
              <div key={a.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 26px)' }}>
                <div className="grid" style={{ '--cols': 'minmax(0, 1.2fr) minmax(0, 1.6fr) 170px', gap: 16, alignItems: 'center' }}>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <div style={{ fontWeight: 600, color: '#003F3A' }}>{a.full_name} · {a.reference}</div>
                    <div style={{ fontSize: 14, color: '#5D645B' }}>{fmtDate(a.created_at)}</div>
                    <div style={{ fontSize: 14, color: '#5D645B' }}>{[a.phone, a.email, a.city].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div style={{ minWidth: 0, fontSize: 15, color: '#5D645B', overflowWrap: 'anywhere' }}>
                    {[a.availability, a.transport, a.vsc_status].filter(Boolean).join(' · ') || '—'}
                    {a.experience ? <div style={{ marginTop: 6 }}>{a.experience}</div> : null}
                    <div style={{ marginTop: 8 }}>
                      {a.resume_filename ? (
                        <a
                          href={`/api/admin/applications/${a.id}/resume`}
                          style={{ color: '#00564F', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >
                          Download resume
                        </a>
                      ) : (
                        <span style={{ fontSize: 14, color: '#967057' }}>No resume on file</span>
                      )}
                      {a.resume_filename && (
                        <span style={{ fontSize: 13, color: '#5D645B' }}>
                          {' '}· {a.resume_filename} ({Math.max(1, Math.round((a.resume_size || 0) / 1024))} KB)
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label style={smallLabel} htmlFor={`ap-${a.id}`}>STATUS</label>
                    <select id={`ap-${a.id}`} style={selectStyle} value={a.status} disabled={busy} onChange={(e) => patchApplication(a.id, { status: e.target.value })}>
                      <option value="new">New</option>
                      <option value="screening">Screening</option>
                      <option value="interviewing">Interviewing</option>
                      <option value="hired">Hired</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'calendar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {openSlots.length === 0 && <p style={{ color: '#5D645B' }}>No requested assessment times on open inquiries.</p>}
            {openSlots.map((s) => (
              <div key={s.key} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: '20px 24px' }}>
                <div className="grid" style={{ '--cols': '220px minmax(0, 1fr) 130px 150px', gap: 16, alignItems: 'center' }}>
                  <div style={{ fontWeight: 600, color: '#003F3A', minWidth: 0, overflowWrap: 'anywhere' }}>{s.slot}</div>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere', color: '#5D645B' }}>{s.who}</div>
                  <span style={{ background: s.status === 'scheduled' ? '#00564F' : '#B7C4B7', color: s.status === 'scheduled' ? '#FAF7F0' : '#003F3A', borderRadius: 4, padding: '6px 12px', fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
                    {s.status}
                  </span>
                  <div style={{ color: '#5D645B', fontSize: 15 }}>{s.assignee || 'Unassigned'}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'schedule' && <ScheduleTab />}
        {tab === 'users' && <UsersTab currentUserId={user?.id} />}
      </div>
    </div>
  )
}
